import React, {
  memo,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  ButtonIcon,
  Icon,
  Link as DSLink,
  Menu,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  ADVANCED_FILTERS_METHODS,
  testIds,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../constants';
import { useInvertedLocalize } from '../../hooks';
import {
  type ActiveAdvancedFilterState,
  type AdvancedFilterState,
} from '../../types';

import { getCustomFilter } from './CustomFilters/SodCustomFilter';
import ChipFilter from './ChipFilter';
import {
  type FilterState,
  type SodAdvancedFiltersSharedFunc,
} from './SodControls';

import './SodAdvancedFilters.css';

interface SodAdvancedFiltersProps extends SodAdvancedFiltersSharedFunc {
  filters: AdvancedFilterState[];
  activeFilters: ActiveAdvancedFilterState[];
  maxEnabledFilterCount?: number;
  searchDebounce: number;
  className?: string;
  isClearBtn?: boolean;
  isAddOrRemoveBtn?: boolean;
  isGraphMode?: boolean;
  isDisabled?: boolean;
  isDarkTheme?: boolean;
}

/**
 * SodAdvancedFilters
 *
 * @description A component that manages advanced filtering capabilities for SOD (Segregation of Duties) system.
 * Provides functionality to add, remove, and manage multiple filter types including custom filters and searchable filters.
 *
 * @typedef {Object} FilterState
 * @property {string} value - Unique identifier for the filter
 * @property {boolean} isChecked - Current selection state of the filter
 * @property {string} method - Filter method type (e.g., 'CUSTOM', 'SEARCH')
 *
 * @typedef {Object} AdvancedFilterState
 * @property {string} value - Unique identifier for the filter
 * @property {string} label - Display label for the filter
 * @property {boolean} isChecked - Whether the filter is currently selected
 * @property {string} method - Filter method type
 * @property {MenuOption[]} [options] - Available options for the filter
 *
 * @param {Object} props - Component props
 * @param {AdvancedFilterState[]} props.filters - Array of filter configurations
 * @param {Array<ActiveAdvancedFilterState>} props.activeFilters - Array of the applied filter
 * @param {number} [props.maxEnabledFilterCount=5] - Maximum number of active filters to display
 * @param {string} [props.className] - Additional CSS class names
 * @param {boolean} [props.isClearBtn=false] - Whether to show the clear filters button
 * @param {boolean} [props.isAddOrRemoveBtn=true] - Weather to show Add Or Remove ButtonisGraphMode
 * @param {boolean} [props.isGraphMode=true] - Weather is graph mode or not
 * @param {number} props.searchDebounce - Debounce delay in milliseconds for search operations
 * @param {boolean} [props.isDarkTheme=false] - Weather dark theme or not
 * @param {(filter: FilterState) => void} props.handleFilterChange - Callback when filter selection changes
 * @param {(options: any) => void} props.handleOptionsClick - Callback when filter options are selected
 * @param {(searchTerm: string, filterKey: string) => void} props.handleFilterSearch - Callback for filter search operations
 * @param {() => void} props.handleClearBtn - Callback when clear button is clicked
 * @param {boolean} [props.isDisabled=false] - Flag to disable all filters
 *
 * @example
 * ```tsx
 * const filters = [
 *   {
 *     value: 'status',
 *     label: 'Status',
 *     isChecked: false,
 *     method: 'CUSTOM',
 *     options: [
 *       { value: 'active', label: 'Active' },
 *       { value: 'inactive', label: 'Inactive' }
 *     ]
 *   }
 * ];
 *
 * <SodAdvancedFilters
 *   filters={filters}
 *   activeFilters={activeFilters}
 *   searchDebounce={300}
 *   handleFilterChange={(filter) => console.log('Filter changed:', filter)}
 *   handleOptionsClick={(options) => console.log('Options selected:', options)}
 *   handleFilterSearch={(term, key) => console.log('Search:', term, 'for', key)}
 *   handleClearBtn={() => console.log('Filters cleared')}
 *   isClearBtn={true}
 *   isAddOrRemoveBtn={isAddOrRemoveBtn}
 * />
 * ```
 *
 * @returns {ReactElement} Rendered component
 *
 * @note
 * - The component supports both custom filters and searchable filters
 * - Custom filters are rendered with a specialized menu interface
 * - Search filters include a debounced search functionality
 * - All filters can be cleared using the clear button (when enabled)
 *
 * @performance
 * - Uses React.memo for component memoization
 * - Implements useMemo for active filters computation
 * - Debounces search operations to prevent excessive API calls
 *
 * @accessibility
 * - Supports keyboard navigation
 * - Includes proper ARIA labels
 * - Follows WCAG guidelines for interactive elements
 */
function SodAdvancedFilters({
  className,
  filters,
  activeFilters = [] as ActiveAdvancedFilterState[],
  maxEnabledFilterCount,
  searchDebounce,
  isClearBtn,
  isAddOrRemoveBtn,
  isGraphMode,
  isDarkTheme,
  handleFilterChange,
  handleOptionsClick,
  handleFilterSearch,
  handleClearBtn,
  isDisabled,
}: SodAdvancedFiltersProps): ReactElement {
  const l = useInvertedLocalize();

  const [filterList, setFilterList] = useState<AdvancedFilterState[]>(filters);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [enabledFilterValue, setEnabledFilterValue] = useState<string | null>(
    null
  );
  const [activeFilterKey, setActiveFilterKey] = useState<string | null>(null);
  const [isWarning, setIsWarning] = useState(false);

  const isEntitlementContext = window.location.search.includes(
    'entitlement_tab_details_page_violations_tab'
  );

  // Setting up filters
  useEffect(() => {
    const updatedFilters = filters.map((filter) => ({
      ...filter,
      isDisabled:
        filter.isDisabled ||
        (isEntitlementContext &&
          (filter.value === 'riskName' ||
            filter.value === 'entitlement_value')),
    }));

    setFilterList(updatedFilters);
  }, [filters, isEntitlementContext]);

  const enabledFilters = useMemo(
    () => filterList.filter((option) => option.isChecked),
    [filterList]
  );
  const isEnabledCount = useMemo(() => {
    const activeCount = filterList.reduce((count, filter) => {
      if (filter.isChecked) {
        return count + 1;
      }

      return count;
    }, 0);

    return activeCount < (maxEnabledFilterCount ?? 999);
  }, [maxEnabledFilterCount, filterList]);
  const isFilterChip = Boolean(enabledFilters.length > 0);

  // Warning section
  useEffect(() => {
    if (!isEnabledCount) {
      setFilterList((currentFilters) =>
        currentFilters.map((filter) => ({
          ...filter,
          isDisabled: !filter.isChecked,
        }))
      );
      setIsWarning(true);

      return;
    }

    setIsWarning(false);
  }, [isEnabledCount]);

  // Filter chip enablement section
  const handleLocalFilterChange = (filter: FilterState) => {
    // Prevent toggling riskName and entitlement_value filters in entitlement tab
    if (
      isEntitlementContext &&
      (filter.value === 'riskName' || filter.value === 'entitlement_value')
    ) {
      return;
    }

    setFilterList((currentFilters) =>
      currentFilters.map((_filter) => ({
        ..._filter,
        isChecked:
          _filter.value === filter.value
            ? !_filter.isChecked
            : _filter.isChecked,
      }))
    );

    handleFilterChange({
      value: filter.value,
      isChecked: filter.isChecked,
      method: filter.method,
    });

    // Only closing when adding filter
    if (!filter.isChecked) {
      setEnabledFilterValue(filter.value);
      setIsMenuExpanded(false);
    }
  };

  const sodControlsClasses = cx('SodAdvancedFilters', className);

  return (
    <Box className={sodControlsClasses}>
      {isFilterChip &&
        enabledFilters.map((filter) => {
          const activeOptions =
            activeFilters.find(
              (activeFilter) => activeFilter.value === filter.value
            )?.options ?? [];

          if (filter.method === ADVANCED_FILTERS_METHODS.CUSTOM) {
            return (
              <React.Fragment key={filter.value}>
                {getCustomFilter(filter.value, {
                  filter,
                  activeOptions,
                  footerLink1Text: l('Remove Filter'),
                  footerLink2Text: l('Clear Selection'),
                  enabledFilterValue,
                  setEnabledFilterValue,
                  handleOptionsClick,
                  handleFilterChange,
                  isGraphMode,
                  isDarkTheme,
                  isDisabled,
                })}
              </React.Fragment>
            );
          }

          return (
            <ChipFilter
              key={filter.value}
              value={filter.value}
              label={filter.label}
              method={filter.method}
              options={filter.options ?? []}
              activeOptions={activeOptions}
              activeFilterKey={activeFilterKey}
              onOpen={setActiveFilterKey}
              enabledFilterValue={enabledFilterValue}
              setEnabledFilterValue={setEnabledFilterValue}
              handleFilterSearch={handleFilterSearch}
              searchDebounce={searchDebounce}
              noDataText={(() => {
                if (filter.method !== ADVANCED_FILTERS_METHODS.SEARCH) {
                  return undefined;
                }

                return filter.value.toLowerCase().includes('application') ||
                  filter.value.toLowerCase().includes('endpoint')
                  ? l('Enter the application name to search')
                  : l('Enter at least 1 character to view options.');
              })()}
              loadingText={l('Loading...')}
              footerLink1Text={
                !isAddOrRemoveBtn ||
                filter.isNoRemoveFilterBtn ||
                (isEntitlementContext &&
                  (filter.value === 'riskName' ||
                    filter.value === 'entitlement_value'))
                  ? ' '
                  : l('Remove Filter')
              }
              footerLink2Text={l('Clear Selection')}
              filterSearchError={(() => {
                if (
                  filter.value ===
                  VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value
                ) {
                  return 'Enter correct endpoint name to search';
                }

                return l('Search failed; please try again');
              })()}
              onFooterLink1Click={handleFilterChange}
              handleOptionsClick={handleOptionsClick}
              handleClearBtn={handleClearBtn}
              isDisabled={
                isEntitlementContext && filter.value === 'entitlement_value'
                  ? true
                  : isDisabled
              }
            />
          );
        })}
      {isAddOrRemoveBtn && (
        <Menu
          kind='addAndRemove'
          label={l('Add or Remove Filters')}
          options={filterList}
          onChange={handleLocalFilterChange}
          isMenuExpanded={isMenuExpanded}
          setIsExpanded={(state: boolean) => {
            setIsMenuExpanded(state);
          }}
          inlineMessageText={l(
            "You've reached maximum number of filter sets allowed. Remove existing ones to add more."
          )}
          isInlineMessageVisible={isWarning}
          onInlineMessageDismiss={() => setIsWarning(false)}
          className='SodAdvancedFilters-addAndRemoveFilterBtn'
          trigger={
            <ButtonIcon
              kind='outlinedNeutral'
              size='medium'
              ariaLabel={l('Add filter')}
              icon={<Icon kind='Add' size='xSmall' />}
              isDisabled={isDisabled}
            />
          }
          shouldCloseMenuOnItemClick={false}
          dataTestId={testIds.SOD_CONTROLS_ADD_REMOVE_BUTTON}
        />
      )}
      {isClearBtn && (
        <DSLink
          text={l('Clear')}
          kind='button'
          className='SodAdvancedFilters-clearBtn'
          onClick={handleClearBtn}
          isDisabled={isDisabled}
        />
      )}
    </Box>
  );
}

SodAdvancedFilters.defaultProps = {
  className: '',
  maxEnabledFilterCount: 999,
  isDarkTheme: false,
  isClearBtn: false,
  isAddOrRemoveBtn: true,
  isGraphMode: false,
  isDisabled: false,
};

export default memo(SodAdvancedFilters);
